import { collection, addDoc, getDocs, doc, getDoc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';

// Collection reference
const categoriesCollection = collection(db, 'categories');

// Interface for category
export interface Category {
  id: string;
  name: string;
  nameEn: string;
  namePt: string;
  slug: string;
  icon?: string;
  order: number;
  active: boolean;
}

// Get all categories
export const getCategories = async (): Promise<Category[]> => {
  try {
    const querySnapshot = await getDocs(query(categoriesCollection, orderBy('order', 'asc')));
    const categories = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Category));
    
    return categories;
  } catch (error) {
    console.error('Error getting categories: ', error);
    // Return empty array on error
    return [];
  }
};

// Get active categories
export const getActiveCategories = async (): Promise<Category[]> => {
  try {
    const categories = await getCategories();
    return categories.filter(category => category.active);
  } catch (error) {
    console.error('Error getting active categories: ', error);
    return [];
  }
};

// Get category by ID
export const getCategoryById = async (id: string): Promise<Category | null> => {
  try {
    const docRef = doc(db, 'categories', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as Category;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting category: ', error);
    return null;
  }
};

// Add a new category
export const addCategory = async (category: Omit<Category, 'id'>): Promise<string> => {
  try {
    const docRef = await addDoc(categoriesCollection, {
      ...category,
      createdAt: new Date()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding category: ', error);
    throw error;
  }
};

// Update a category
export const updateCategory = async (id: string, data: Partial<Category>): Promise<void> => {
  try {
    const docRef = doc(db, 'categories', id);
    await updateDoc(docRef, data);
  } catch (error) {
    console.error('Error updating category: ', error);
    throw error;
  }
};

// Delete a category
export const deleteCategory = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, 'categories', id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting category: ', error);
    throw error;
  }
};